// OnboardingChat.tsx — the web self-onboarding chat surface (S4 4.W3).
//
// A visitor who tapped "אני חדש" (from AuthScreen) or "להצטרף" (from GuestChat)
// has a placeholder member session (POST /onboard/start created the user +
// landed the cookie). GET /me reports `{ onboarding: true }`, so the AuthGate
// renders this surface. It drives the LLM onboarding agent over POST
// /onboard-chat (set_name → optional provide_phone → complete_onboarding); the
// conversation lives in memory and is replayed via `history`, exactly like
// GuestChat. The opening "what should I call you?" bubble is a static client
// turn — the first real round-trip carries the visitor's name. When the backend
// reports `onboarded: true` (complete_onboarding fired), we reload into the full
// member chat. Branded via the CSS custom properties applied at boot by brand.ts.

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getOrgConfig } from '../brand.ts';

const MAX_MESSAGE_LEN = 10_000;
// Mirror the backend's MAX_HISTORY_TURNS — only the recent window is replayed.
const HISTORY_TURNS = 12;

type OnboardTurn = {
  role: 'user' | 'assistant';
  id: string;
  text: string;
  pending?: boolean;
};

export const OnboardingChat = () => {
  const cfg = getOrgConfig();
  const orgName = cfg.brand?.name ?? cfg.name ?? 'דרישלום';

  // The opening agent turn is static (the backend onboarding agent only speaks
  // once it has the visitor's first message). It is NOT replayed as history —
  // history starts from the visitor's first real turn.
  const [turns, setTurns] = useState<OnboardTurn[]>([
    {
      role: 'assistant',
      id: 'welcome',
      text: 'ברוכים הבאים 👋 איך לקרוא לך?',
    },
  ]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo({
      top: scroller.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [turns.length, pending]);

  const send = async (text: string): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed || pending) return;
    setDraft('');
    setError(null);
    setPending(true);

    const userTurn: OnboardTurn = {
      role: 'user',
      id: crypto.randomUUID(),
      text: trimmed,
    };
    const pendingId = crypto.randomUUID();
    // History the backend sees = real turns BEFORE this message. The static
    // 'welcome' bubble is excluded (the agent generates its own greeting flow).
    const history = turns
      .filter((t) => !t.pending && t.id !== 'welcome')
      .slice(-HISTORY_TURNS)
      .map((t) => ({ role: t.role, text: t.text }));
    setTurns((t) => [
      ...t,
      userTurn,
      { role: 'assistant', id: pendingId, text: '', pending: true },
    ]);

    try {
      const res = await fetch('/onboard-chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ message: trimmed, history }),
        credentials: 'include',
      });
      if (res.status === 401) {
        // The session lapsed mid-onboarding — back to the entry surface.
        window.location.assign('/');
        return;
      }
      if (res.status === 429) {
        setTurns((t) => t.filter((turn) => turn.id !== pendingId));
        setError('יותר מדי הודעות. המתינו רגע ונסו שוב.');
        setPending(false);
        return;
      }
      if (!res.ok) throw new Error(`http_${res.status}`);
      const data = (await res.json()) as { text?: string; onboarded?: boolean };
      const reply = typeof data.text === 'string' ? data.text : '';
      setTurns((t) =>
        t.map((turn) =>
          turn.id === pendingId
            ? { role: 'assistant', id: pendingId, text: reply }
            : turn,
        ),
      );
      // complete_onboarding fired — hand off to the member chat. Briefly let the
      // welcome reply paint, then reload (GET /me now returns a member identity).
      if (data.onboarded === true) {
        window.setTimeout(() => window.location.assign('/'), 1200);
      }
    } catch {
      setTurns((t) =>
        t.map((turn) =>
          turn.id === pendingId
            ? { role: 'assistant', id: pendingId, text: 'שגיאת רשת. נסו שוב.' }
            : turn,
        ),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center justify-center border-b border-[var(--color-border)] bg-[var(--color-card)] px-4">
        <h1 className="text-base font-bold text-[var(--color-primary)]">
          {orgName}
        </h1>
      </header>

      <main
        ref={scroller}
        className="flex-1 overflow-y-auto px-4 py-3"
        aria-live="polite"
      >
        <div className="mx-auto max-w-[720px]">
          <ul className="space-y-3">
            {turns.map((t) => (
              <OnboardBubble key={t.id} turn={t} />
            ))}
          </ul>
          {error ? (
            <div
              role="alert"
              className="mx-auto mt-3 max-w-[85%] rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2 text-center text-sm text-[var(--color-bad)]"
            >
              {error}
            </div>
          ) : null}
        </div>
      </main>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send(draft);
        }}
        className="border-t border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2"
      >
        <div className="mx-auto flex max-w-[720px] items-stretch gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE_LEN))}
            maxLength={MAX_MESSAGE_LEN}
            placeholder="כתבו כאן..."
            aria-label="הודעה"
            className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-start outline-none focus:border-[var(--color-primary)]"
            dir="rtl"
            autoFocus
          />
          <button
            type="submit"
            disabled={!draft.trim() || pending}
            className="cursor-pointer rounded-lg bg-[var(--color-primary)] px-4 text-sm font-bold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[var(--color-border)] disabled:text-[var(--color-muted)] disabled:opacity-100 disabled:hover:opacity-100"
          >
            שלחו
          </button>
        </div>
      </form>
    </div>
  );
};

const OnboardBubble = ({ turn }: { turn: OnboardTurn }) => {
  const isUser = turn.role === 'user';
  // Match the member chat alignment: user visual-RIGHT, agent visual-LEFT.
  const align = isUser ? 'flex justify-start' : 'flex justify-end';
  if (!isUser && turn.pending) {
    return (
      <li className={`${align} items-end gap-2`}>
        <div
          className="max-w-[85%] rounded-2xl bg-[var(--color-primary-light)] px-4 py-2 text-sm text-[var(--color-text)] shadow-[var(--shadow-bubble)]"
          role="status"
          aria-label="חושבת..."
        >
          <span className="inline-flex gap-1" aria-hidden="true">
            <span className="thinking-dot [animation-delay:0ms]" />
            <span className="thinking-dot [animation-delay:150ms]" />
            <span className="thinking-dot [animation-delay:300ms]" />
          </span>
        </div>
      </li>
    );
  }
  return (
    <li className={`${align} items-end gap-2`}>
      <div
        className={
          isUser
            ? 'max-w-[85%] rounded-2xl bg-[var(--color-user-bubble)] px-4 py-2 text-sm text-white shadow-[var(--shadow-bubble)]'
            : 'max-w-[85%] rounded-2xl bg-[var(--color-primary-light)] px-4 py-2 text-sm text-[var(--color-text)] shadow-[var(--shadow-bubble)]'
        }
      >
        {isUser ? (
          <div>{turn.text}</div>
        ) : (
          <div className="[&_p]:my-1 [&_strong]:font-bold [&_ul]:my-1 [&_ul]:list-disc [&_ul]:ps-5 [&_a]:text-[var(--color-primary)] [&_a]:underline [&>:first-child]:mt-0 [&>:last-child]:mb-0">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.text}</ReactMarkdown>
          </div>
        )}
      </div>
    </li>
  );
};
